import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ChevronRight,
  Heart,
  Camera,
  Frame,
  Palette,
  Clock,
  Award
} from 'lucide-react';

const PetPhotography = () => {
  const features = [
    {
      icon: Heart,
      title: "Emotional Connection",
      desc: "Capturing the unique personality and soul of your companion."
    },
    {
      icon: Camera,
      title: "Studio Quality",
      desc: "Professional lighting and direction for gallery-worthy results."
    },
    {
      icon: Frame,
      title: "Wall Art Ready",
      desc: "High-resolution images designed for large format prints."
    },
    {
      icon: Palette,
      title: "Artistic Options",
      desc: "Classic portraits to painterly fine-art interpretations."
    },
    {
      icon: Clock,
      title: "Patient Process",
      desc: "Relaxed sessions allowing pets to feel comfortable."
    },
    {
      icon: Award,
      title: "Archival Prints",
      desc: "Museum-quality printing options for lasting keepsakes."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section - Solid Navy Background */}
      <section className="hero-main relative h-[300px] md:h-[340px] bg-[#050816]">
        {/* Subtle top gradient for navbar contrast */}
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-black/30 to-transparent"></div>
      </section>

      {/* Breadcrumb Section */}
      <section className="bg-[#FAF9F6] border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-5 md:py-6">
          <div className="flex items-center gap-3">
            <Link 
              to="/services" 
              className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#F5C056] hover:text-[#F5C056]/80 transition-colors"
            >
              Services
            </Link>
            <span className="text-[#050816]/30 text-xs">/</span>
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#050816]">
              Pet Photography
            </span>
          </div>
          <div className="w-12 h-[1.5px] bg-[#F5C056]/40 mt-4"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-4">Pet Portraits</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#050816] mb-4">
              Fine Art Pet Photography
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Fine art portraits honoring the companions who share our lives. Timeless, dignified, and designed to be treasured.
            </p>
          </div>

          {/* CTA Button */}
          <div className="text-center mb-12">
            <Button 
              asChild 
              className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all shadow-lg hover:shadow-xl"
            >
              <Link to="/book?type=other">
                Book a Session
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-[#FAF9F6]">
        <div className="container mx-auto px-6 max-w-[1200px]">
          
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase">Gallery</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">Fine Art Pet Portraits</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From classic studio portraits to artistic interpretations — each piece is crafted for gallery-grade presentation.
            </p>
          </div>

          {/* Portrait Gallery Grid */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 mb-16">
            
            {/* Classic Portrait */}
            <div className="group">
              <div className="relative overflow-hidden rounded-xl shadow-xl bg-white p-4">
                <div className="overflow-hidden rounded-lg">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/5829123a-ab4f-4aa7-ae16-b569c5612100/images/f7f99e59293004e22812edde795d9b26e4d4cd2b1a5a3a83abfa0e79af560129.png"
                    alt="Fine art German Shepherd portrait"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                </div>
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-lg font-semibold text-foreground mb-2">Classic Portraiture</h3>
                <p className="text-sm text-muted-foreground">
                  Timeless painterly realism with rich warm tones. Designed for large wall prints.
                </p>
              </div>
            </div>

            {/* Artistic Splash Portrait */}
            <div className="group">
              <div className="relative overflow-hidden rounded-xl shadow-xl bg-white p-4">
                <div className="overflow-hidden rounded-lg">
                  <img 
                    src="https://static.prod-images.emergentagent.com/jobs/5829123a-ab4f-4aa7-ae16-b569c5612100/images/035e09ec5566be04faa9c948ed0bf6c42703de872893624926582b14674640f1.png"
                    alt="Artistic watercolor pet portrait"
                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                </div>
              </div>
              <div className="mt-6 text-center">
                <h3 className="text-lg font-semibold text-foreground mb-2">Artistic Expression</h3>
                <p className="text-sm text-muted-foreground">
                  Painterly realism with tasteful artistic color accents for visual impact.
                </p>
              </div>
            </div>

          </div>

          {/* Additional Reference Images */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="group overflow-hidden rounded-lg shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/rpvedfba_ChatGPT%20Image%20Feb%207%2C%202026%2C%2001_24_29%20AM.png"
                alt="Watercolor splash pet portrait"
                className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="group overflow-hidden rounded-lg shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/13se8y1t_ChatGPT%20Image%20Feb%207%2C%202026%2C%2001_23_29%20AM.png"
                alt="Artistic pet portrait"
                className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="group overflow-hidden rounded-lg shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/9cgmxkiq_ChatGPT%20Image%20Feb%207%2C%202026%2C%2001_24_46%20AM.png"
                alt="Pet and owner portrait"
                className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
            <div className="group overflow-hidden rounded-lg shadow-md">
              <img 
                src="https://customer-assets.emergentagent.com/job_rcstudioapp/artifacts/fa41o6nc_B4CD0278-0637-4BBB-A856-DD6F55D0EBCA_1_105_c.jpeg"
                alt="Dramatic pet portrait"
                className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          </div>

        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-[900px]">
          <div className="text-center space-y-6">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">
              More Than a Photo
            </h2>
            <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
              <p>
                Our pets aren&apos;t just animals — they&apos;re family. They greet us at the door, comfort us in quiet moments, and bring joy in ways that words can&apos;t fully capture. A portrait is a way to honor that bond.
              </p>
              <p>
                I approach pet photography the same way I approach portraiture: with patience, intention, and a focus on capturing something real. Whether it&apos;s the dignified gaze of a senior dog or the curious expression of a new puppy, every session is about finding that moment of connection.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#FAF9F6]">
        <div className="container mx-auto px-6 max-w-[1100px]">
          <div className="text-center mb-14 space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">The Experience</h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Every session is designed for comfort, quality, and lasting results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((item, i) => (
              <Card key={i} className="border-none shadow-sm hover:shadow-md transition-all duration-300 bg-white">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-full bg-[#F5C056]/10 flex items-center justify-center mb-4 text-[#F5C056]">
                    <item.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Styles Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6 max-w-[900px]">
          <div className="text-center mb-14 space-y-4">
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">Portrait Styles</h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto">
              Choose the presentation that best honors your companion.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-[#FAF9F6] rounded-xl p-8 border border-gray-100">
              <h3 className="text-xl font-semibold text-foreground mb-4">Classic Studio Portrait</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Traditional portraiture with refined lighting and neutral backgrounds. Timeless elegance designed to complement any interior.
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5C056]"></span>
                  Professional studio lighting
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5C056]"></span>
                  Warm neutral tones
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5C056]"></span>
                  Gallery-ready finish
                </li>
              </ul>
            </div>

            <div className="bg-[#FAF9F6] rounded-xl p-8 border border-gray-100">
              <h3 className="text-xl font-semibold text-foreground mb-4">Fine Art & Artistic</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Painterly interpretations with artistic elements. Color splashes, watercolor effects, and creative finishes for a unique statement piece.
              </p>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5C056]"></span>
                  Painterly realism
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5C056]"></span>
                  Artistic color accents
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F5C056]"></span>
                  Custom creative direction
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#050816] text-white">
        <div className="container mx-auto px-6 text-center max-w-[700px]">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">
            Honor Your Companion
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Let&apos;s create a portrait that captures what makes them special.
          </p>
          <Button 
            asChild 
            className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-10 text-sm uppercase tracking-widest font-medium transition-all shadow-lg hover:shadow-xl"
          >
            <Link to="/book?type=other">
              Book Your Session
              <ChevronRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PetPhotography;
